// ═══════════════════════════════════════════════════════════════════════════
//  ExecutionArmBar — arm, then hold to fire. Mobile execution, Phase 7.
//  God 2026-09-04: "Enable both PAPER + LIVE execution ... I understand the
//  risks, but the Super Admin requires full control on the go."
//
//  This bar does NOT place orders and does not know how to. It calls the page's
//  existing fire function, which emits a SIGNAL into the same router the
//  TradingView webhooks use — so every gate the desktop honours (BOT_STOPPED,
//  trade windows, L/P/T limits, JIT depth, the TRADING_MODE master kill-switch)
//  applies unchanged. That was the blueprint's rule for this phase: if it is
//  not the same function, it does not ship.
//
//  Two locks, because a phone is not a desk:
//
//   1. ARM — nothing can fire while disarmed, and arming EXPIRES. The window is
//      idle-based: every fire restarts it, so an active session stays armed and
//      a phone put back in a pocket disarms itself. A permanent arm is just an
//      un-armed button with extra steps.
//   2. HOLD — the fire control is a sustained press, not a tap. A pocket can
//      tap. Cancelling is free; completing takes intent.
//
//  The LIVE lane is deliberately harder than PAPER: a longer hold and the
//  destructive colour, because the consequence is different and the UI should
//  say so before the order does.
//
//  HONEST LIMIT, recorded here so it is not lost: the loss-cap kill switch that
//  would brake a runaway counts LIVE trades only, so on the 100%-paper fleet it
//  reads zero and has never fired in anger. God has that in writing and chose
//  to proceed; it still needs one real firing test before mobile LIVE execution
//  can be called DONE under RULE #40.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HoldButton } from '../components/ui/HoldButton';

/** Idle window. A fire restarts it; silence ends it. */
export const ARM_WINDOW_MS = 5 * 60 * 1000;

export interface ExecutionArmBarProps {
  lane: 'PAPER' | 'LIVE';
  /** The page's own fire function. This component never builds an order. */
  onFire: (action: 'BUY' | 'SELL') => void;
  /** Symbol/side context for the label — display only. */
  contextLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export const ExecutionArmBar: React.FC<ExecutionArmBarProps> = ({
  lane, onFire, contextLabel, disabled = false, disabledReason,
}) => {
  const isLive = lane === 'LIVE';
  const [armedUntil, setArmedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // One ticker, only while armed — a disarmed bar costs nothing.
  useEffect(() => {
    if (armedUntil == null) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    timerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };
  }, [armedUntil]);

  const armed = armedUntil != null && armedUntil > now;
  const leftMs = armed ? Math.max(0, (armedUntil as number) - now) : 0;
  const mmss = `${String(Math.floor(leftMs / 60000)).padStart(2, '0')}:${String(Math.floor((leftMs % 60000) / 1000)).padStart(2, '0')}`;

  // Expiry is derived from the clock, not from a setTimeout that a suspended
  // phone would fire late. Once past, drop the state so the ticker stops.
  useEffect(() => { if (armedUntil != null && armedUntil <= now) setArmedUntil(null); }, [armedUntil, now]);

  const arm = useCallback(() => { setNow(Date.now()); setArmedUntil(Date.now() + ARM_WINDOW_MS); }, []);
  const disarm = useCallback(() => setArmedUntil(null), []);

  const fire = useCallback((action: 'BUY' | 'SELL') => {
    if (!armed || disabled) return;
    onFire(action);
    setArmedUntil(Date.now() + ARM_WINDOW_MS);   // active use keeps it armed
  }, [armed, disabled, onFire]);

  const tone = isLive ? 'neg' : 'pos';

  return (
    // data-jc-armed (God 2026-09-15, iOS audit): the shell's update logic reads
    // this so a new-build reload can never fire while a hold is armed.
    <div className="jc-glass" data-jc-armed={armed ? '1' : '0'} style={{
      padding: 'var(--jc-s-3)', display: 'flex', flexDirection: 'column', gap: 'var(--jc-s-2)',
      borderColor: isLive ? 'rgba(255,77,106,.30)' : 'var(--jc-hairline)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jc-s-2)', flexWrap: 'wrap' }}>
        <span className={`jc-pill ${isLive ? 'warn' : 'info'}`}>
          <span className="dot" />{isLive ? '🔱 LIVE · REAL MONEY' : '🧪 PAPER'}
        </span>
        {contextLabel && (
          <span className="jc-num" style={{ fontSize: 11, color: 'var(--jc-text-dim)' }}>{contextLabel}</span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          {armed
            ? <span className="jc-pill live"><span className="dot" />armed · {mmss}</span>
            : <span className="jc-pill off"><span className="dot" />disarmed</span>}
        </span>
      </div>

      {disabled && disabledReason && (
        <div style={{ fontSize: 10, color: 'var(--jc-warn)', lineHeight: 1.5 }}>{disabledReason}</div>
      )}

      {!armed ? (
        <button
          type="button"
          className="jc-icon-btn jc-press"
          onClick={arm}
          disabled={disabled}
          style={{ minHeight: 46, width: '100%', justifyContent: 'center', letterSpacing: 1.2 }}>
          ARM EXECUTION · {Math.round(ARM_WINDOW_MS / 60000)} MIN
        </button>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--jc-s-2)' }}>
            <HoldButton
              tone={tone}
              disabled={disabled}
              holdMs={isLive ? 1400 : 900}
              holdingLabel="HOLD"
              onConfirm={() => fire('BUY')}>
              ⬆ BUY
            </HoldButton>
            <HoldButton
              tone="neg"
              disabled={disabled}
              holdMs={isLive ? 1400 : 900}
              holdingLabel="HOLD"
              onConfirm={() => fire('SELL')}>
              ⬇ EXIT / SELL
            </HoldButton>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="jc-label">
              hold {isLive ? '1.4s' : '0.9s'} to fire · release to cancel
            </span>
            <button type="button" onClick={disarm} className="jc-icon-btn"
              style={{ marginLeft: 'auto', minHeight: 44, minWidth: 64, fontSize: 11 }}>
              DISARM
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExecutionArmBar;
